import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import heroRibbonsUrl from '../../assets/brand/hero-ribbons.svg';
import { tokens } from '../../theme';
import AvatarIcon from '../AvatarIcon';

interface MobileHeroProps {
  name: string;
  voiceEnabled?: boolean;
}

const styles = {
  card: {
    position: 'relative',
    overflow: 'hidden',
    flexShrink: 0,
    background:
      'linear-gradient(to bottom right, var(--avatar-color-brand-red) 0%, color-mix(in srgb, var(--avatar-color-brand-red) 58%, white) 100%)',
    borderRadius: tokens.radius.xl,
    padding: tokens.space[4],
    color: tokens.text.onBrand,
  },
  pattern: {
    position: 'absolute',
    top: '50%',
    left: '68%',
    width: '175%',
    height: '175%',
    transform: 'translate(-50%, -50%) rotate(-45deg)',
    objectFit: 'cover',
    objectPosition: 'center',
    pointerEvents: 'none',
    opacity: 0.14,
    WebkitMaskImage:
      'radial-gradient(ellipse 72% 72% at 50% 50%, #000 18%, transparent 72%)',
    maskImage:
      'radial-gradient(ellipse 72% 72% at 50% 50%, #000 18%, transparent 72%)',
  },
  content: {
    position: 'relative',
    zIndex: 1,
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.space[3],
  },
  badge: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: tokens.surface.elevated,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  copy: {
    flex: 1,
    minWidth: 0,
  },
  hello: {
    margin: 0,
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: 6,
    fontSize: 16,
    fontWeight: tokens.font.weight.regular,
    lineHeight: tokens.font.lineHeight.tight,
  },
  helloText: {
    fontWeight: tokens.font.weight.bold,
  },
  body: {
    margin: `${tokens.space[2]} 0 0`,
    fontSize: 16,
    fontWeight: tokens.font.weight.regular,
    lineHeight: tokens.font.lineHeight.body,
    opacity: 0.95,
  },
  divider: {
    height: 1,
    margin: `${tokens.space[4]} 0`,
    background: tokens.text.onBrand,
    opacity: 0.35,
    border: 'none',
  },
  hint: {
    margin: 0,
    fontSize: 16,
    fontWeight: tokens.font.weight.regular,
    lineHeight: tokens.font.lineHeight.body,
    opacity: 0.9,
  },
  wave: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji"',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSynthesis: 'none',
    lineHeight: 1,
  },
} as const satisfies Record<string, CSSProperties>;

export default function MobileHero({ name, voiceEnabled }: MobileHeroProps) {
  const { t } = useTranslation();

  return (
    <section style={styles.card} aria-label={t('mobile.hello', { name })}>
      <img
        src={heroRibbonsUrl}
        alt=""
        aria-hidden="true"
        style={styles.pattern}
      />
      <div style={styles.content}>
        <div style={styles.row}>
          <div style={styles.badge}>
            <AvatarIcon variant="accent" size={34} alt="" />
          </div>
          <div style={styles.copy}>
            <h2 style={styles.hello}>
              <span style={styles.helloText}>{t('mobile.hello', { name })}</span>
              <span style={styles.wave} aria-hidden="true">
                {'\u{1F44B}'}
              </span>
            </h2>
            <p style={styles.body}>{t('mobile.heroBody', { name })}</p>
          </div>
        </div>
        <div style={styles.divider} />
        <p style={styles.hint}>
          {t(voiceEnabled ? 'mobile.heroHintVoice' : 'mobile.heroHint')}
        </p>
      </div>
    </section>
  );
}
